import { defineComponent as _defineComponent } from 'vue';
import { storeToRefs } from "pinia";
import { computed, inject, reactive, ref } from "vue";
import { useAnimationFrameSize } from "@/composables/sensors/animationFrameSize";
import { useAnimationFrameThrottle } from "@/composables/throttle";
import { useWorkflowStores } from "@/composables/workflowStores";
import { LazyMoveMultipleAction } from "./Actions/workflowActions";
import { useMultiSelect } from "./composables/multiSelect";
import { useDraggable } from "./composables/useDraggable";
export default /*#__PURE__*/ _defineComponent({
    __name: 'Draggable',
    props: {
        rootOffset: {
            type: Object,
            required: true,
        },
        position: {
            type: Object,
            default: null,
        },
        preventDefault: {
            type: Boolean,
            default: true,
        },
        stopPropagation: {
            type: Boolean,
            default: true,
        },
        dragData: {
            type: Object,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        snappable: {
            type: Boolean,
            default: true,
        },
        selected: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["mousedown", "mouseup", "move", "dragstart", "start", "stop"],
    setup(__props, { emit }) {
        const props = __props;
        let dragImg = null;
        const draggable = ref();
        const size = reactive(useAnimationFrameSize(draggable));
        const transform = inject("transform");
        const { throttle } = useAnimationFrameThrottle();
        let dragging = false;
        let hasMoved = false;
        const { anySelected, multiSelectedSteps, multiSelectedComments, deselectAll } = useMultiSelect();
        const previousPosition = ref(null);
        const currentLazyAction = ref(null);
        const shouldMultidrag = computed(() => props.selected && anySelected.value);
        const onStart = (_position, event) => {
            emit("mousedown", event);
            hasMoved = false;
            if (!shouldMultidrag.value) {
                emit("start");
            }
            if (event.type == "dragstart") {
                dragImg = document.createElement("img");
                dragImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                document.body.appendChild(dragImg);
                // I guess better than copy ?
                event.dataTransfer.effectAllowed = "link";
                try {
                    event.dataTransfer.setDragImage(dragImg, 0, 0);
                }
                catch (e) {
                    console.error(e);
                }
                if (props.dragData) {
                    event.dataTransfer.setData("text/plain", JSON.stringify(props.dragData));
                }
                if (!shouldMultidrag.value) {
                    emit("dragstart", event);
                }
            }
        };
        const { toolbarStore, stepStore, commentStore, undoRedoStore } = useWorkflowStores();
        const { snapActive } = storeToRefs(toolbarStore);
        function getSnappedPosition(position) {
            if (props.snappable && snapActive.value) {
                return Object.assign(Object.assign({}, position), { x: Math.round(position.x / toolbarStore.snapDistance) * toolbarStore.snapDistance, y: Math.round(position.y / toolbarStore.snapDistance) * toolbarStore.snapDistance });
            }
            else {
                return Object.assign(Object.assign({}, position), { x: position.x, y: position.y });
            }
        }
        const onMove = (position, event) => {
            dragging = true;
            toolbarStore.inputCatcherTemporarilyDisabled = true;
            if (event.type == "drag" && event.x == 0 && event.y == 0) {
                // the last drag event has no coordinate ... this is obviously a hack!
                return;
            }
            throttle(() => {
                var _a, _b;
                if (dragging) {
                    const newPosition = {
                        unscaled: Object.assign(Object.assign({}, position), size),
                        x: (position.x - props.rootOffset.x - transform.value.x) / transform.value.k,
                        y: (position.y - props.rootOffset.y - transform.value.y) / transform.value.k,
                    };
                    const snapped = getSnappedPosition(newPosition);
                    if (!previousPosition.value ||
                        previousPosition.value.x !== snapped.x ||
                        previousPosition.value.y !== snapped.y) {
                        if (shouldMultidrag.value) {
                            if (undoRedoStore.isQueued(currentLazyAction.value)) {
                                (_a = currentLazyAction.value) === null || _a === void 0 ? void 0 : _a.changePosition(snapped);
                            }
                            else {
                                currentLazyAction.value = new LazyMoveMultipleAction(commentStore, stepStore, multiSelectedComments.value, multiSelectedSteps.value, (_b = props.position) !== null && _b !== void 0 ? _b : newPosition, snapped);
                                undoRedoStore.applyLazyAction(currentLazyAction.value);
                            }
                        }
                        else {
                            emit("move", snapped, event);
                        }
                        hasMoved = true;
                    }
                    previousPosition.value = snapped;
                }
            });
        };
        const onEnd = (_position, event) => {
            toolbarStore.inputCatcherTemporarilyDisabled = false;
            if (dragImg) {
                document.body.removeChild(dragImg);
                dragImg = null;
            }
            if (!hasMoved && !event.shiftKey) {
                deselectAll();
            }
            dragging = false;
            emit("mouseup", event);
            if (!shouldMultidrag.value) {
                emit("stop");
            }
        };
        const maybeDraggable = computed(() => (props.disabled ? null : draggable.value));
        useDraggable(maybeDraggable, {
            preventDefault: props.preventDefault,
            stopPropagation: props.stopPropagation,
            useCapture: false,
            onStart: onStart,
            onMove: onMove,
            onEnd: onEnd,
        });
        return { __sfc: true, props, emit, dragImg, draggable, size, transform, throttle, dragging, hasMoved, anySelected, multiSelectedSteps, multiSelectedComments, deselectAll, previousPosition, currentLazyAction, shouldMultidrag, onStart, toolbarStore, stepStore, commentStore, undoRedoStore, snapActive, getSnappedPosition, onMove, onEnd, maybeDraggable };
    }
});
